import { useCallback, useEffect, useRef, useState } from 'react';
import AlertList from './AlertList.jsx';
import ChartCard from './ChartCard.jsx';
import ErrorState from './ErrorState.jsx';
import KpiGrid from './KpiGrid.jsx';
import ModuleFilters from './ModuleFilters.jsx';
import PageHeader from './PageHeader.jsx';
import SectionHeader from './SectionHeader.jsx';
import SimpleBarList from './SimpleBarList.jsx';
import DualBarList from './DualBarList.jsx';
import SimplePieChart from './SimplePieChart.jsx';
import SimpleTable from './SimpleTable.jsx';
import SourceBanner from './SourceBanner.jsx';
import { useModuleFilters } from '../hooks/useModuleFilters.js';
import { useAnalyticsRefresh } from '../hooks/useAnalyticsRefresh.js';

/**
 * Shell de dashboard de módulo (fuente aún no conectada o payload de servicio).
 * loadData(filters) — filters.force=true al pulsar Actualizar (invalida caché local 2 h).
 */
export default function ModuleDashboardPage({
  title,
  subtitle,
  email,
  loadData,
  filterOpts = {},
  sections = [],
  persistKey
}) {
  const withOriginTags = !!(filterOpts && filterOpts.showOriginTags);
  const filters = useModuleFilters({
    persistKey: persistKey || 'mod:' + String(title || 'x').toLowerCase(),
    withOriginTags
  });
  const [loading, setLoading] = useState(false);
  const [error, setError] = useState('');
  const [data, setData] = useState(null);
  const [search, setSearch] = useState('');
  const [tick, setTick] = useState(0);
  const [hasSearched, setHasSearched] = useState(false);
  const forceRef = useRef(false);
  const loadSeq = useRef(0);

  useAnalyticsRefresh(
    useCallback(() => {
      if (!hasSearched) return;
      forceRef.current = true;
      setTick((t) => t + 1);
    }, [hasSearched])
  );

  useEffect(() => {
    if (!hasSearched) return;
    let cancelled = false;
    const seq = ++loadSeq.current;
    if (withOriginTags && !filters.originTagsReady) {
      setLoading(true);
      return undefined;
    }
    const force = forceRef.current;
    forceRef.current = false;

    (async () => {
      setLoading(true);
      setError('');
      try {
        const payload = await loadData({
          period: filters.period,
          dateFrom: filters.range.dateFrom,
          dateTo: filters.range.dateTo,
          executiveId: filters.executiveId,
          projectId: filters.projectId,
          channel: filters.channel,
          status: filters.status,
          originTags: withOriginTags ? filters.originTags : undefined,
          force
        });
        if (cancelled || seq !== loadSeq.current) return;
        setData(payload);
      } catch (err) {
        if (cancelled || seq !== loadSeq.current) return;
        setError((err && err.message) || 'Error al cargar el módulo.');
        if (!data) setData(null);
      } finally {
        if (!cancelled && seq === loadSeq.current) setLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps -- data intencional fuera para no re-fetch en loop
  }, [tick]);

  function runSearch(force) {
    setHasSearched(true);
    forceRef.current = force !== false;
    setTick((t) => t + 1);
  }

  function cancelSearch() {
    loadSeq.current += 1;
    setLoading(false);
  }

  const catalogs = (data && data.catalogs) || {};
  const tableRows = ((data && data.table && data.table.rows) || []).filter((row) => {
    if (!search) return true;
    const q = search.toLowerCase();
    return Object.values(row).some((v) => String(v || '').toLowerCase().includes(q));
  });

  const cacheNote =
    data && data._fromCache
        ? ' · caché local'
        : data && data.message && /Caché servidor/i.test(data.message)
        ? ' · caché servidor'
        : '';

  return (
    <div className={'analytics-module analytics-module-shell'}>
      <PageHeader
        eyebrow={'Módulo · ' + title}
        title={title}
        subtitle={subtitle + (email ? ` · ${email}` : '') + cacheNote}
        actions={
          <ModuleFilters
            filters={filters}
            disabled={false}
            busy={loading}
            onRefresh={() => runSearch(true)}
            onCancel={cancelSearch}
            executives={catalogs.executives || []}
            projects={catalogs.projects || []}
            channels={catalogs.channels || []}
            statuses={catalogs.statuses || []}
            originTagOptions={filters.originTagOptions || []}
            originTagsLoading={filters.originTagsLoading}
            {...filterOpts}
          />
        }
      />

      <SourceBanner
        mode={(data && data.sourceStatus) || (loading ? 'ok' : 'unconnected')}
        message={(data && (data.message || data.note)) || ''}
      />

      {!hasSearched && !loading ? (
        <p className="analytics-empty">
          Elegí el período y pulsá <strong>Buscar</strong>. No se consulta hasta entonces.
        </p>
      ) : null}

      <ErrorState
        message={error}
        onRetry={() => runSearch(true)}
      />

      <section className="analytics-section">
        <SectionHeader
          title="Indicadores"
          note={
            data && data.sourceStatus === 'ok'
              ? 'Datos de la fuente (caché local 2 h; Buscar en la barra recarga).'
              : data && data.sourceStatus === 'error'
                ? 'La consulta falló; los valores pueden faltar.'
                : 'Sin fuente conectada — se muestra Sin datos (no ceros inventados).'
          }
        />
        <KpiGrid items={(data && data.kpis) || []} loading={loading && !data} />
      </section>

      {sections.map((sec) => {
        if (sec.type === 'alerts') {
          return (
            <section key={sec.id} className="analytics-section">
              <SectionHeader title={sec.title} note={sec.note} />
              <div className="analytics-panel">
                <AlertList
                  items={(data && data.alerts) || []}
                  emptyLabel={
                    data && data.alertPlaceholders && data.alertPlaceholders.length
                      ? 'Sin alertas (reglas listas: ' + data.alertPlaceholders.join(', ') + ').'
                      : 'Sin alertas activas.'
                  }
                />
              </div>
            </section>
          );
        }
        if (sec.type === 'table') {
          return (
            <section key={sec.id} className="analytics-section">
              <SectionHeader title={sec.title} note={sec.note} />
              <SimpleTable
                columns={(data && data.table && data.table.columns) || []}
                rows={tableRows}
                search={search}
                onSearchChange={setSearch}
                emptyText="Sin filas. Este listado se completará al conectar la fuente."
              />
            </section>
          );
        }
        const chartKey = sec.chartKey || sec.id;
        const rows =
          chartKey === 'funnel'
            ? (data && data.funnel) || []
            : (data && data.charts && data.charts[chartKey]) || [];
        const isPie = sec.chartType === 'pie';
        const isDual = sec.chartType === 'dual';
        return (
          <ChartCard key={sec.id} title={sec.title} note={sec.note}>
            {loading && !data ? (
              <p className="analytics-empty">Cargando…</p>
            ) : isDual ? (
              <DualBarList rows={rows} />
            ) : isPie ? (
              <SimplePieChart rows={rows} valueSuffix={sec.valueSuffix || ''} />
            ) : (
              <SimpleBarList rows={rows} />
            )}
          </ChartCard>
        );
      })}
    </div>
  );
}
